import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from './Navbar';

const AdminPage = () => {
  const navigate = useNavigate();
  const [stats] = useState({
    totalUsers: 2847,
    activeServices: 156,
    totalWorkflows: 892,
    systemHealth: 99.8,
    monthlyRevenue: 284750,
    supportTickets: 23
  });

  const recentActivities = [
    { id: 1, message: 'New user registration: john.doe@company.com', time: '2 minutes ago', status: 'success', icon: 'person_add' },
    { id: 2, message: 'Service deployment: Restaurant AI v2.1', time: '15 minutes ago', status: 'success', icon: 'rocket_launch' },
    { id: 3, message: 'High CPU usage detected on Server-03', time: '1 hour ago', status: 'warning', icon: 'warning' },
    { id: 4, message: 'Workflow "Order Processing" completed successfully', time: '2 hours ago', status: 'success', icon: 'check_circle' },
    { id: 5, message: 'Database backup completed', time: '3 hours ago', status: 'success', icon: 'cloud_done' }
  ];

  const quickActions = [
    { title: 'User Management', description: 'Manage users and permissions', icon: 'group', link: '/admin/users', gradient: 'linear-gradient(135deg, #13a4ec, #0e7fc0)' },
    { title: 'System Settings', description: 'Configure system parameters', icon: 'settings', link: '/admin/settings', gradient: 'linear-gradient(135deg, #10b981, #059669)' },
    { title: 'AI Agents', description: 'Manage AI agent configurations', icon: 'smart_toy', link: '/ai-agents', gradient: 'linear-gradient(135deg, #7c3aed, #5b21b6)' },
    { title: 'Analytics', description: 'View system analytics', icon: 'analytics', link: '/admin/analytics', gradient: 'linear-gradient(135deg, #f97316, #ea580c)' },
    { title: 'Security', description: 'Security and compliance', icon: 'shield', link: '/admin/security', gradient: 'linear-gradient(135deg, #ef4444, #dc2626)' },
    { title: 'Logs & Insights', description: 'System logs and insights', icon: 'monitoring', link: '/logs', gradient: 'linear-gradient(135deg, #6366f1, #4f46e5)' }
  ];

  const statCards = [
    { label: 'Total Users', value: stats.totalUsers.toLocaleString(), icon: 'group', gradient: 'linear-gradient(135deg, #13a4ec, #0e7fc0)', glow: 'rgba(19,164,236,0.3)', change: '+12%', up: true },
    { label: 'Active Services', value: stats.activeServices, icon: 'hub', gradient: 'linear-gradient(135deg, #10b981, #059669)', glow: 'rgba(16,185,129,0.3)', change: '+3', up: true },
    { label: 'Total Workflows', value: stats.totalWorkflows, icon: 'account_tree', gradient: 'linear-gradient(135deg, #7c3aed, #5b21b6)', glow: 'rgba(124,58,237,0.3)', change: '+28', up: true },
    { label: 'System Health', value: `${stats.systemHealth}%`, icon: 'verified_user', gradient: 'linear-gradient(135deg, #10b981, #059669)', glow: 'rgba(16,185,129,0.3)', change: 'Healthy', up: true },
    { label: 'Monthly Revenue', value: `$${stats.monthlyRevenue.toLocaleString()}`, icon: 'payments', gradient: 'linear-gradient(135deg, #f59e0b, #d97706)', glow: 'rgba(245,158,11,0.3)', change: '+18%', up: true },
    { label: 'Support Tickets', value: stats.supportTickets, icon: 'support_agent', gradient: 'linear-gradient(135deg, #f97316, #ea580c)', glow: 'rgba(249,115,22,0.3)', change: '-5', up: false }
  ];

  const getStatusColor = (status) => {
    if (status === 'success') return '#10b981';
    if (status === 'warning') return '#f59e0b';
    return '#ef4444';
  };

  return (
    <div className="min-h-screen font-display" style={{ backgroundColor: '#101c22', color: '#fff' }}>
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">

        {/* Page header */}
        <div className="mb-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #13a4ec, #7c3aed)' }}>
                <span className="material-symbols-outlined text-white text-xl">admin_panel_settings</span>
              </div>
              <h1 className="text-3xl font-extrabold text-white">Admin Dashboard</h1>
            </div>
            <p className="text-gray-400 ml-13">Full control over your OrbitMind platform.</p>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 rounded-xl border border-white/10" style={{ backgroundColor: 'rgba(16,185,129,0.08)' }}>
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-emerald-400 text-sm font-semibold">All Systems Operational</span>
          </div>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-10">
          {statCards.map((stat, i) => (
            <div
              key={i}
              className="rounded-2xl border border-white/10 p-5 transition-all hover:-translate-y-0.5 hover:border-white/20"
              style={{ backgroundColor: 'rgba(255,255,255,0.03)' }}
            >
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ background: stat.gradient, boxShadow: `0 0 16px ${stat.glow}` }}>
                <span className="material-symbols-outlined text-white text-base">{stat.icon}</span>
              </div>
              <p className="text-2xl font-extrabold text-white mb-0.5">{stat.value}</p>
              <p className="text-xs text-gray-500 mb-2">{stat.label}</p>
              <span className={`text-xs font-semibold ${stat.up ? 'text-emerald-400' : 'text-red-400'}`}>{stat.change}</span>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">

          {/* Quick Actions */}
          <div className="rounded-2xl border border-white/10 p-6" style={{ backgroundColor: 'rgba(255,255,255,0.03)' }}>
            <h2 className="text-lg font-bold text-white mb-5 flex items-center gap-2">
              <span className="material-symbols-outlined text-primary text-xl">bolt</span>
              Quick Actions
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {quickActions.map((action, i) => (
                <Link
                  key={i}
                  to={action.link}
                  className="flex items-center gap-4 p-4 rounded-xl border border-white/10 hover:border-white/20 hover:bg-white/[0.04] transition-all group"
                >
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform" style={{ background: action.gradient }}>
                    <span className="material-symbols-outlined text-white text-base">{action.icon}</span>
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">{action.title}</p>
                    <p className="text-gray-500 text-xs">{action.description}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="rounded-2xl border border-white/10 p-6" style={{ backgroundColor: 'rgba(255,255,255,0.03)' }}>
            <h2 className="text-lg font-bold text-white mb-5 flex items-center gap-2">
              <span className="material-symbols-outlined text-primary text-xl">sensors</span>
              Recent Activity
            </h2>
            <div className="space-y-3">
              {recentActivities.map((activity) => (
                <div key={activity.id} className="flex items-start gap-3 p-3 rounded-xl border border-white/5 hover:border-white/10 transition-colors" style={{ backgroundColor: 'rgba(255,255,255,0.02)' }}>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${getStatusColor(activity.status)}20` }}>
                    <span className="material-symbols-outlined text-sm" style={{ color: getStatusColor(activity.status), fontSize: '16px' }}>{activity.icon}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm leading-snug">{activity.message}</p>
                    <p className="text-gray-500 text-xs mt-0.5">{activity.time}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-white/5">
              <Link to="/logs" className="text-sm font-semibold hover:opacity-80 transition-opacity flex items-center gap-1" style={{ color: '#13a4ec' }}>
                View all activity
                <span className="material-symbols-outlined text-base">arrow_forward</span>
              </Link>
            </div>
          </div>
        </div>

        {/* System Status */}
        <div className="rounded-2xl border border-white/10 p-6" style={{ backgroundColor: 'rgba(255,255,255,0.03)' }}>
          <h2 className="text-lg font-bold text-white mb-5 flex items-center gap-2">
            <span className="material-symbols-outlined text-primary text-xl">monitor_heart</span>
            System Status
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { label: 'API Gateway', status: 'Operational', uptime: '99.9%', icon: 'api' },
              { label: 'Database Cluster', status: 'Operational', uptime: '100%', icon: 'storage' },
              { label: 'AI Services', status: 'Operational', uptime: '99.8%', icon: 'smart_toy' }
            ].map((service, i) => (
              <div key={i} className="flex items-center justify-between p-4 rounded-xl border border-white/10" style={{ backgroundColor: 'rgba(255,255,255,0.02)' }}>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.2), rgba(5,150,105,0.2))', border: '1px solid rgba(16,185,129,0.2)' }}>
                    <span className="material-symbols-outlined text-emerald-400 text-base">{service.icon}</span>
                  </div>
                  <div>
                    <p className="text-white text-sm font-semibold">{service.label}</p>
                    <p className="text-emerald-400 text-xs font-medium">{service.status}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-white text-sm font-bold">{service.uptime}</p>
                  <div className="flex items-center gap-1 justify-end">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    <span className="text-gray-500 text-xs">Live</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
};

export default AdminPage;
